/**
 * Teacher Payload Utility Functions
 * Handles construction and validation of teacher analytics payloads
 * Based on the validation documentation for different feedback types
 */

/**
 * Formats month and year into MMM-YYYY format
 * @param {string|number} month - Month (1-12 or "1"-"12")
 * @param {string|number} year - Year (e.g., "2025")
 * @returns {string} Formatted month_year in MMM-YYYY format (e.g., "Aug-2025")
 */
export const formatMonthYear = (month, year) => {
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const monthNum = parseInt(month, 10);
  if (monthNum < 1 || monthNum > 12) {
    throw new Error(`Invalid month: ${month}. Month must be between 1 and 12.`);
  }

  const monthName = monthNames[monthNum - 1];
  return `${monthName}-${year}`;
};

/**
 * Constructs teacher payload based on feedback type and time period
 * @param {string} timePeriod - The time period (year, month, week, custom)
 * @param {string} feedbackType - The feedback type (overall, event, single_overall, single_event)
 * @param {object} context - Context object containing required data
 * @returns {object} Constructed payload
 */
export const constructTeacherPayload = (timePeriod, feedbackType, context) => {
  const basePayload = {
    ...(context.teacher_id && { teacher_id: context.teacher_id }),
    ...(context.student_id && { student_id: context.student_id }),
    courses: context.selectedCourseData,
    students: context.selectedStudentData,
    avg_score: context.avg_score,
    time_period: timePeriod,
    overall_feedback:
      feedbackType === "overall" || feedbackType === "single_overall",
    event_overview: feedbackType === "event" || feedbackType === "single_event",
    single_overall_feedback: feedbackType === "single_overall",
    single_event_overview: feedbackType === "single_event",
  };

  // Helper function to validate and format month_year
  const validateAndFormatMonthYear = (monthYear, month, year) => {
    // If monthYear is already in correct format (MMM-YYYY), return it
    if (monthYear && /^[A-Za-z]{3}-\d{4}$/.test(monthYear)) {
      return monthYear;
    }

    // If monthYear is just a year (like "2025"), format it with current month
    if (monthYear && /^\d{4}$/.test(monthYear)) {
      return formatMonthYear(month, monthYear);
    }

    // If monthYear is empty or invalid, format with current month and year
    if (month && year) {
      return formatMonthYear(month, year);
    }

    return monthYear; // Return as is if we can't format it
  };

  // Helper function to format daily_date to DD-MM-YYYY format
  const formatDailyDate = (dailyDate, month, year) => {
    if (!dailyDate) return dailyDate;
    
    // If already in DD-MM-YYYY format, return as is
    if (/^\d{2}-\d{2}-\d{4}$/.test(dailyDate)) {
      return dailyDate;
    }
    
    // If in "DD MMM" format (e.g., "28 Aug"), convert to DD-MM-YYYY
    const match = dailyDate.match(/^(\d{1,2})\s+([A-Za-z]{3})$/);
    if (match) {
      const day = match[1].padStart(2, '0');
      
      // If month and year are provided, use them
      if (month && year) {
        const monthStr = month.toString().padStart(2, '0');
        return `${day}-${monthStr}-${year}`;
      }
      
      // If month and year are not provided, try to get current month/year
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();
      const monthStr = currentMonth.toString().padStart(2, '0');
      return `${day}-${monthStr}-${currentYear}`;
    }
    
    // If in other formats, try to parse and format
    try {
      const date = new Date(dailyDate);
      if (!isNaN(date.getTime())) {
        const day = date.getDate().toString().padStart(2, '0');
        const monthStr = (date.getMonth() + 1).toString().padStart(2, '0');
        const yearStr = date.getFullYear().toString();
        return `${day}-${monthStr}-${yearStr}`;
      }
    } catch (e) {
      // If parsing fails, return as is
    }
    
    return dailyDate;
  };

  switch (timePeriod) {
    case "year":
      if (feedbackType === "single_event") {
        // For single_event with year time period, exclude month_year to get specific event feedback
        // instead of yearly feedback when clicking on dots
        const payload = {
          ...basePayload,
          year: context.year,
          daily_date: context.daily_date || context.startdate || context.year,
        };

        // month_year is excluded for single_event in year time period to get specific event feedback
        return payload;
      } else if (feedbackType === "single_overall") {
        // For single_overall_feedback = true AND overall_feedback = true
        const formattedMonthYear = formatMonthYear(context.month, context.year);
        return {
          ...basePayload,
          year: context.year,
          month: context.month,
          month_year: formattedMonthYear,
        };
      } else {
        // For overall feedback with year time period, include both year and month_year
        const payload = {
          ...basePayload,
          year: context.year,
        };

        // Always ensure month_year is included for year time period
        if (context.month_year) {
          payload.month_year = validateAndFormatMonthYear(
            context.month_year,
            context.month,
            context.year
          );
        } else if (context.month && context.year) {
          // If not provided but month and year are available, format it
          payload.month_year = formatMonthYear(context.month, context.year);
        } else if (context.year) {
          // If only year is available, use current month with the year
          const currentMonth = new Date().getMonth() + 1; // 1-12
          payload.month_year = formatMonthYear(currentMonth, context.year);
        }

        return payload;
      }

    case "month":
      if (feedbackType === "single_event") {
        // For single_event with month time period, exclude month_year and week_start_date to get specific event feedback
        const payload = {
          ...basePayload,
          year: context.year,
          month: context.month,
          daily_date: context.daily_date || context.startdate || context.year,
        };

        // month_year and week_start_date are excluded for single_event to get specific event feedback
        return payload;
      } else if (feedbackType === "single_overall") {
        // For single_overall_feedback = true AND overall_feedback = true
        // Prioritize actual week start date from dot/filter, only use fallback if no dot data
        let weekStartDate = context.week_start_date || context.startdate;
        // Only use fallback if we have no dot data (week_start_date is empty/undefined)
        // and no startdate provided
        if ((!weekStartDate || weekStartDate === "") && context.month && context.year) {
          // Fallback: use first day of the month in DD-MM-YYYY format
          weekStartDate = `01-${context.month.toString().padStart(2, '0')}-${context.year}`;
        }
        
        const formattedMonthYear = formatMonthYear(context.month, context.year);
        return {
          ...basePayload,
          year: context.year,
          month: context.month,
          month_year: formattedMonthYear,
          week_start_date: weekStartDate,
        };
      } else {
        // For overall feedback, include month_year if provided in context
        // Prioritize actual week start date from dot/filter, only use fallback if no dot data
        let weekStartDate = context.week_start_date || context.startdate;
        // Only use fallback if we have no dot data (week_start_date is empty/undefined)
        // and no startdate provided
        if ((!weekStartDate || weekStartDate === "") && context.month && context.year) {
          // Fallback: use first day of the month in DD-MM-YYYY format
          weekStartDate = `01-${context.month.toString().padStart(2, '0')}-${context.year}`;
        }
        
        const payload = {
          ...basePayload,
          year: context.year,
          month: context.month,
          week_start_date: weekStartDate,
        };

        // Validate and format month_year if provided in context
        if (context.month_year) {
          payload.month_year = validateAndFormatMonthYear(
            context.month_year,
            context.month,
            context.year
          );
        } else if (context.month && context.year) {
          // If not provided but month and year are available, format it
          payload.month_year = formatMonthYear(context.month, context.year);
        }

        return payload;
      }

    case "week":
      if (feedbackType === "single_event") {
        // For single_event with week time period, exclude month_year to get specific event feedback
        const payload = {
          ...basePayload,
          year: context.year,
          month: context.month,
          daily_date: context.daily_date || context.startdate,
        };

        // month_year is excluded for single_event to get specific event feedback
        return payload;
      } else if (feedbackType === "single_overall") {
        // For single_overall_feedback = true AND overall_feedback = true
        // As per docs, for week period daily_date is required
        // Prioritize actual daily_date from dot/filter, only use fallback if no dot data
        let dailyDate = context.daily_date || context.startdate;
        // Only use fallback if we have no dot data (daily_date is empty/undefined)
        // and no startdate provided
        if ((!dailyDate || dailyDate === "") && context.month && context.year) {
          // Fallback: use first day of the month in DD-MM-YYYY format
          dailyDate = `01-${context.month.toString().padStart(2, '0')}-${context.year}`;
        }
        
        return {
          ...basePayload,
          year: context.year,
          month: context.month,
          daily_date: dailyDate,
        };
      } else {
        // For overall feedback, include month_year if provided in context
        // Prioritize actual daily_date from dot/filter, only use fallback if no dot data
        let dailyDate = context.daily_date || context.startdate;
        // Only use fallback if we have no dot data (daily_date is empty/undefined)
        // and no startdate provided
        if ((!dailyDate || dailyDate === "") && context.month && context.year) {
          // Fallback: use first day of the month in DD-MM-YYYY format
          dailyDate = `01-${context.month.toString().padStart(2, '0')}-${context.year}`;
        }
        
        const payload = {
          ...basePayload,
          year: context.year,
          month: context.month,
          daily_date: dailyDate,
        };

        // Validate and format month_year if provided in context
        if (context.month_year) {
          payload.month_year = validateAndFormatMonthYear(
            context.month_year,
            context.month,
            context.year
          );
        } else if (context.month && context.year) {
          // If not provided but month and year are available, format it
          payload.month_year = formatMonthYear(context.month, context.year);
        }

        return payload;
      }

    case "custom":
      if (feedbackType === "single_event") {
        // For single_event with custom time period, exclude start_date, end_date, and month_year
        // to get specific event feedback for the clicked dot
        const payload = {
          ...basePayload,
          daily_date: formatDailyDate(context.daily_date || context.startdate, context.month, context.year),
        };

        // start_date, end_date, and month_year are excluded for single_event to get specific event feedback
        return payload;
      } else if (feedbackType === "single_overall") {
        // For single_overall_feedback = true AND overall_feedback = true
        // For custom period, exclude start_date and end_date for dot feedback
        // Note: month_year is excluded from websocket payload for custom time period
        return {
          ...basePayload,
          // start_date and end_date are excluded for single_overall feedback in custom time period
          daily_date: formatDailyDate(context.daily_date || context.startdate, context.month, context.year),
        };
      } else {
        // For overall feedback, include month_year if provided in context
        const payload = {
          ...basePayload,
          start_date: context.startdate,
          end_date: context.enddate,
          daily_date: formatDailyDate(context.daily_date || context.startdate, context.month, context.year),
        };

        // Validate and format month_year if provided in context
        if (context.month_year) {
          payload.month_year = validateAndFormatMonthYear(
            context.month_year,
            context.month,
            context.year
          );
        } else if (context.month && context.year) {
          // If not provided but month and year are available, format it
          payload.month_year = formatMonthYear(context.month, context.year);
        }

        return payload;
      }

    default:
      return basePayload;
  }
};

/**
 * Validates teacher payload according to the validation rules from documentation
 * @param {object} payload - The payload to validate
 * @returns {boolean} True if valid, throws error if invalid
 * @throws {Error} Validation error with specific message
 */
export const validateTeacherPayload = (payload) => {
  const {
    time_period,
    overall_feedback,
    event_overview,
    single_overall_feedback,
    single_event_overview,
    year,
    month,
    start_date,
    end_date,
    daily_date,
    courses,
    students,
    teacher_id,
    student_id,
  } = payload;

  // Basic required fields - either teacher_id or student_id must be present
  if ((!teacher_id && !student_id) || !courses || !students) {
    throw new Error(
      "Either teacher_id or student_id, courses, and students are required"
    );
  }

  // Condition 1: Single Overall Feedback + Overall Feedback (Both True)
  if (single_overall_feedback === true && overall_feedback === true) {
    switch (time_period) {
      case "year":
        if (!year || !month) {
          throw new Error("month and year are required");
        }
        break;
      case "month":
        if (!payload.week_start_date) {
          throw new Error("week_start_date is required");
        }
        break;
      case "custom":
        if (!daily_date) {
          throw new Error("daily_date is required");
        }
        break;
      case "week":
        if (!daily_date) {
          throw new Error("daily_date is required");
        }
        break;
    }
  }

  // Condition 2: Overall Feedback OR Event Overview (Either True)
  if (overall_feedback === true || event_overview === true) {
    switch (time_period) {
      case "year":
        if (!year) {
          throw new Error("year is required");
        }
        break;
      case "month":
        if (!month || !year) {
          throw new Error("month and year are required");
        }
        break;
      case "week":
        if (!month || !year) {
          throw new Error("month and year are required");
        }
        break;
      case "custom":
        // For single feedback types, start_date and end_date are not required
        // Only required for regular overall/event feedback (not single)
        if (!single_overall_feedback && !single_event_overview) {
          if (!start_date || !end_date) {
            throw new Error("start_date and end_date are required");
          }
        }
        break;
    }
  }

  // Condition 3: Single Event Overview + Event Overview (Both True)
  if (single_event_overview === true && event_overview === true) {
    if (!daily_date) {
      throw new Error("daily_date is required");
    }
    // month_year is not required for single_event feedback to get specific event feedback
  }

  return true;
};

/**
 * Creates payload for Overall Performance feedback
 * @param {object} context - Context object with required data
 * @returns {object} Validated payload for overall performance
 */
export const createOverallPerformancePayload = (context) => {
  const payload = constructTeacherPayload(
    context.periodTab,
    "overall",
    context
  );
  validateTeacherPayload(payload);
  return payload;
};

/**
 * Creates payload for Events Overview feedback
 * @param {object} context - Context object with required data
 * @returns {object} Validated payload for events overview
 */
export const createEventsOverviewPayload = (context) => {
  const payload = constructTeacherPayload(context.periodTab, "event", context);
  validateTeacherPayload(payload);
  return payload;
};

/**
 * Creates payload for Single Overall Feedback
 * @param {object} context - Context object with required data
 * @returns {object} Validated payload for single overall feedback
 */
export const createSingleOverallFeedbackPayload = (context) => {
  const payload = constructTeacherPayload(
    context.periodTab,
    "single_overall",
    context
  );
  validateTeacherPayload(payload);
  return payload;
};

/**
 * Creates payload for Single Event Feedback
 * @param {object} context - Context object with required data
 * @returns {object} Validated payload for single event feedback
 */
export const createSingleEventFeedbackPayload = (context) => {
  const payload = constructTeacherPayload(
    context.periodTab,
    "single_event",
    context
  );
  validateTeacherPayload(payload);
  return payload;
};
